import { ChangeDetectorRef, Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { LocalDate } from '../../../core/model/local-date';
import { ReportService } from '../../service/data/report.service';
import { Subscription } from 'rxjs';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { CompanyService } from '../../../rent/service/data/company.service';
import { PropertyService } from '../../../rent/service/data/property.service';
import { RentalObjectCategory } from '../../../rent/model/const/enums';
import { ProfileService } from '../../../common/service/data/profile.service';
import ListViewColumn from '../../../core/model/list-view-column';
import { canSubmitReportForm } from '../../utility/report-form-validation';

@Component({
    selector: 'app-checkin-and-checkout',
    templateUrl: './checkin-and-checkout.component.html',
    styleUrls: ['./checkin-and-checkout.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CheckInAndCheckOutComponent implements OnDestroy, OnInit {
  constructor(
    private _cdf: ColumnDefinitionFactory,
    private _dataService: ReportService,
    private _nativeWindow: NativeWindowService,
    private _companyService: CompanyService,
    private _propertyService: PropertyService,
    private _profileService: ProfileService,
    fb: FormBuilder,
    fcf: FormControlExFactory,
    private cdr: ChangeDetectorRef
  ) {
    this.companyService = _companyService;
    this.propertyService = _propertyService;

    let startPeriod = new LocalDate(LocalDate.today().year, LocalDate.today().month, 15);
    let endPeriod = LocalDate.fromDate(new LocalDate(LocalDate.today().year, LocalDate.today().month, 14).addMonths(1));

    this.form = fb.group({
      company: fcf.create('COMMON.COMPANY', []),
      property: fcf.create('COMMON.PROPERTY', []),
      periodFrom: fcf.createMandatory('INVOICE.PERIOD_FROM', startPeriod),
      periodTo: fcf.createMandatory('INVOICE.PERIOD_TO', endPeriod),
      rentalObjectCategories: fcf.create('COMMON.RENTAL_OBJECT_CATEGORY', [RentalObjectCategory.Dwelling]),
    });
  }

  private _subscription: Subscription;
  propertyService: PropertyService;
  companyService: CompanyService;
  columnDefinitions: ColumnDefinition[];
  rentalObjectCategorySelection: { name: string; value: number }[];
  columns: ListViewColumn[];
  dataContext: [];
  form: FormGroup;
  filter: any = {};
  get excelContext() {
    return 'CheckInAndCheckOut';
  }

  ngOnInit() {
    this.createColumnDefinitions();
    this.rentalObjectCategorySelection = [{ name: 'CONST.RENTAL_OBJECT_CATEGORY_DWELLING', value: RentalObjectCategory.Dwelling }];
    this.rentalObjectCategorySelection.push({ name: 'CONST.RENTAL_OBJECT_CATEGORY_PREMISES', value: RentalObjectCategory.Premises });
    this.rentalObjectCategorySelection.push({ name: 'CONST.RENTAL_OBJECT_CATEGORY_PARKING', value: RentalObjectCategory.Parking });
    this.rentalObjectCategorySelection.push({ name: 'CONST.RENTAL_OBJECT_CATEGORY_OTHER', value: RentalObjectCategory.Other });
  }

  private createColumnDefinitions() {
    this.columnDefinitions = [
      this._cdf.create('REPORT.MOVINGIN_MOVINGOUT_LABEL', 'inOutLabel'),
      this._cdf.create('COMMON.COMPANY', 'companyNumberAndName', ColumnVisibility.optional),
      this._cdf.create('COMMON.PROPERTY', 'propertyNumberAndName', ColumnVisibility.optional),
      this._cdf.create('RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN', 'rentalId'),
      this._cdf.create('MAIL_LIST.STREET_COLUMN', 'street'),
      this._cdf.createDecimal('COMMON.AREA', 'surface', ColumnVisibility.optional),
      this._cdf.create('LEASE_AGREEMENT.TENANT_ONE', 'tenantOneName'),
      this._cdf.create('LEASE_AGREEMENT.TENANT_ONE_CIVICNUMBER', 'tenantOneContactIdentification', ColumnVisibility.optional),
      this._cdf.create('LEASE_AGREEMENT.TENANT_ONE_PHONE', 'tenantOnePhone'),
      this._cdf.create('LEASE_AGREEMENT.TENANT_ONE_EMAIL', 'tenantOneEmail', ColumnVisibility.optional),
      this._cdf.create('LEASE_AGREEMENT.TENANT_TWO', 'tenantTwoName', ColumnVisibility.optional),
      this._cdf.create('LEASE_AGREEMENT.TENANT_TWO_CIVICNUMBER', 'tenantTwoContactIdentification', ColumnVisibility.optional),
      this._cdf.create('LEASE_AGREEMENT.TENANT_TWO_PHONE', 'tenantTwoPhone', ColumnVisibility.optional),
      this._cdf.create('LEASE_AGREEMENT.TENANT_TWO_EMAIL', 'tenantTwoEmail', ColumnVisibility.optional),
      this._cdf.createDate('COMMON.MOVING_IN_DATE', 'movingInDate'),
      this._cdf.createDate('COMMON.MOVING_OUT_DATE', 'movingOutDate'),
      this._cdf.createMoney('LEASE_AGREEMENT.DEPOSIT_AMOUNT', 'depositAmount', ColumnVisibility.optional),
      this._cdf.createMoney('COMMON.YEAR_AMOUNT', 'yearAmount', ColumnVisibility.optional),
      this._cdf.createMoney('COMMON.YEAR_RENT', 'yearAmountRent', ColumnVisibility.optional),
    ];
  }

  ngOnDestroy(): void {
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
  }

  exportExcel(): void {
    const formValue = this.EnsureCategorySelected();
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
    this._subscription = this._dataService.getCheckInAndCheckOutExcel(formValue).subscribe(file => {
      this._nativeWindow.save(file);
    });
  }

  onPrintPages() {
    if (!canSubmitReportForm(this.form)) return;
    window.print();
  }

  onSubmit() {
    if (!canSubmitReportForm(this.form)) return;
    const formValue = this.EnsureCategorySelected();
    if (this._subscription) this._subscription.unsubscribe();
    this._subscription = this._dataService.getCheckInCheckOutReport(formValue).subscribe(data => {
      this.dataContext = data;
    });
  }

  get selectedColumns(): ListViewColumn[] {
    return this.columns?.filter(x => x.selected);
  }

  onColumnChange(columns: ListViewColumn[]) {
    this.columns = columns;
    this.cdr.detectChanges();
  }

  private EnsureCategorySelected() {
    const formValue = { ...this.form.value };
    if (!formValue.rentalObjectCategories || formValue.rentalObjectCategories.length === 0) {
      formValue.rentalObjectCategories = [RentalObjectCategory.Dwelling];
    } else if (!Array.isArray(formValue.rentalObjectCategories)) {
      formValue.rentalObjectCategories = [formValue.rentalObjectCategories];
    }
    return formValue;
  }
}
